import { AlertTriangle, ChevronDown, ChevronRight, ClipboardX } from "lucide-react";
import { useMemo } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { type BadgeStatus, StatusBadge } from "@/components/ui/status-badge";
import { type DemandRow, isNosUnavailable, useDemands } from "@/hooks/use-demands";
import { type DemandFilterState, matchesDemandFilters } from "@/lib/demand-filters";
import { cn } from "@/lib/utils";
import { DemandResourceGrid } from "./DemandResourceGrid";
import { formatDateMonth } from "./month-format";

const ROW_COLS = "grid-cols-[minmax(240px,2fr)_minmax(160px,1fr)_112px_minmax(150px,0.8fr)_88px]";

const STATUS_TONE: Record<string, BadgeStatus> = {
  Active: "brand",
  Planning: "pending",
  Proposed: "pending",
  Completed: "completed",
  Cancelled: "disabled",
};

// Long lists render in pages of this size so the first view stays light.
const RENDER_LIMIT = 200;

/** The Demands list: filtered client-side, each row expanding into its Resource grid. */
export function DemandsTable({
  filters,
  setFilters,
}: {
  filters: DemandFilterState;
  setFilters: (next: DemandFilterState) => void;
}) {
  const { data, isLoading, error, refetch } = useDemands();

  const filtered = useMemo<DemandRow[]>(
    () => (data?.demands ?? []).filter((d) => matchesDemandFilters(d, filters)),
    [data, filters],
  );

  const toggleOpen = (id: string) =>
    setFilters({
      ...filters,
      open: filters.open.includes(id)
        ? filters.open.filter((v) => v !== id)
        : [...filters.open, id],
    });

  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground">
        Loading Demands from NOS…
      </div>
    );
  }

  if (error || !data) {
    if (isNosUnavailable(error)) {
      return (
        <div className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-950">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            NOS isn't connected, so Demands can't be loaded. The rest of the app works as normal. (
            {error?.message})
          </span>
        </div>
      );
    }
    return (
      <ErrorState message={error?.message ?? "Failed to load Demands."} onRetry={() => refetch()} />
    );
  }

  if (data.demands.length === 0) {
    return (
      <EmptyState
        icon={ClipboardX}
        title="No Demands to show"
        description="There are no NOS Demands on the Accounts you can see."
      />
    );
  }

  const visible = filtered.slice(0, RENDER_LIMIT);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {filtered.length} of {data.demands.length} Demands
        {filtered.length > RENDER_LIMIT
          ? ` · showing the first ${RENDER_LIMIT} — narrow the search to see more`
          : ""}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <div className="min-w-[820px]">
          <div
            className={cn(
              "grid items-center gap-3 border-b border-border bg-secondary px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground",
              ROW_COLS,
            )}
          >
            <span>Demand</span>
            <span>Account</span>
            <span>Status</span>
            <span>Dates</span>
            <span className="text-right">Resources</span>
          </div>
          <div className="divide-y divide-border">
            {visible.map((d) => {
              const open = filters.open.includes(d.id);
              return (
                <div key={d.id}>
                  <button
                    type="button"
                    onClick={() => toggleOpen(d.id)}
                    aria-expanded={open}
                    className={cn(
                      "grid w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-brand-50/60",
                      ROW_COLS,
                      open && "bg-brand-50/40",
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {open ? (
                        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate font-display text-sm font-semibold">{d.name}</span>
                    </span>
                    <span className="truncate text-sm text-muted-foreground">
                      {d.accountName ?? "—"}
                    </span>
                    <span>
                      {d.status ? (
                        <StatusBadge status={STATUS_TONE[d.status] ?? "disabled"}>
                          {d.status}
                        </StatusBadge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatDateMonth(d.startDate)} –{" "}
                      {d.endDate ? formatDateMonth(d.endDate) : "Ongoing"}
                    </span>
                    <span className="text-right font-mono text-sm tabular-nums">
                      {d.resourceCount}
                    </span>
                  </button>
                  {open && (
                    <div className="border-t border-border bg-background">
                      <DemandResourceGrid demandId={d.id} />
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                No Demands match. Try another search or clear the filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
